import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTablePreferences } from "./utils/useTablePreferences";
import { fetchCourses } from "../pages/services/courseService";
import "../styles/ListPage.css";

const CourseList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL;

  const [courses, setCourses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage, sortConfig, setSortConfig } = useTablePreferences("table_prefs_courses");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");

  const filterChangedRef = useRef(false);

  useEffect(() => {
    if (location.state?.successMessage) {
      setToast(location.state.successMessage);
      window.history.replaceState({}, "");
      setTimeout(() => setToast(""), 3000);
    }
  }, [location.state]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const json = await fetchCourses({ page: page + 1, pageSize: rowsPerPage, search: searchTerm });
      let data = json.data || [];

      if (sortConfig.column && sortConfig.direction) {
        data = [...data].sort((a, b) => {
          const av = a[sortConfig.column] ?? "";
          const bv = b[sortConfig.column] ?? "";
          const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
          return sortConfig.direction === "asc" ? cmp : -cmp;
        });
      }

      setCourses(data);
      setTotalCount(json.pagination?.totalItems || data.length);
    } catch (err) {
      console.error("Error fetching courses:", err);
    }
    setLoading(false);
  }, [page, rowsPerPage, searchTerm, sortConfig]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (filterChangedRef.current) { setPage(0); filterChangedRef.current = false; }
  }, [searchTerm]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const onSort = (column) => {
    setPage(0);
    setSortConfig((prev) => {
      if (prev.column === column) {
        if (prev.direction === "asc") return { column, direction: "desc" };
        if (prev.direction === "desc") return { column: null, direction: null };
      }
      return { column, direction: "asc" };
    });
  };

  const thClass = (col) => sortConfig.column === col
    ? (sortConfig.direction === "asc" ? "lp-sort-asc" : "lp-sort-desc")
    : "";

  const handleRowClick = (id) => navigate(`/courses/${id}`);
  const handleAddCourse = () => navigate("/courses/add");

  const formatFee = (fee) => fee != null ? `₹${Number(fee).toLocaleString("en-IN")}` : "—";

  return (
    <div className="lp-page">
      {toast && <div className="lp-toast">{toast}</div>}

      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">All Courses</h2>
          <span className="lp-count">{totalCount} records</span>
        </div>
        <div className="lp-header-actions">
          <button className="lp-btn-primary" onClick={handleAddCourse}>+ Add Course</button>
        </div>
      </div>

      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input
            type="text"
            placeholder="Search courses…"
            value={searchTerm}
            onChange={(e) => { filterChangedRef.current = true; setSearchTerm(e.target.value); }}
            className="lp-search"
          />
        </div>
      </div>

      <div className="lp-table-card">
        {loading ? (
          <div className="lp-state"><div className="lp-spinner" /><p>Loading courses…</p></div>
        ) : courses.length === 0 ? (
          <div className="lp-state"><p>No courses found.</p></div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                <th className={thClass("courseTitle")} onClick={() => onSort("courseTitle")}>Course Name</th>
                <th className={thClass("currentBatches")} onClick={() => onSort("currentBatches")}>Current Batches</th>
                <th className={thClass("completedBatches")} onClick={() => onSort("completedBatches")}>Batches Completed</th>
                <th className={thClass("courseDuration")} onClick={() => onSort("courseDuration")}>Duration</th>
                <th className={thClass("courseFee")} onClick={() => onSort("courseFee")}>Fees</th>
                <th className={thClass("studentsEnrolled")} onClick={() => onSort("studentsEnrolled")}>Students Enrolled</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course._id} onClick={() => handleRowClick(course._id)}>
                  <td style={{ fontWeight: 600, color: "#111827" }}>{course.courseTitle || "—"}</td>
                  <td>
                    <span style={{ background: "#dcfce7", color: "#16a34a", padding: "2px 10px", borderRadius: 10, fontSize: 12, fontWeight: 700 }}>
                      {course.currentBatches}
                    </span>
                  </td>
                  <td>
                    <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "2px 10px", borderRadius: 10, fontSize: 12, fontWeight: 700 }}>
                      {course.completedBatches}
                    </span>
                  </td>
                  <td>{course.courseDuration || "—"}</td>
                  <td>{formatFee(course.courseFee)}</td>
                  <td>
                    <span style={{ background: "#ede9fe", color: "#5b21b6", padding: "2px 10px", borderRadius: 10, fontSize: 12, fontWeight: 700 }}>
                      {course.studentsEnrolled}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && courses.length > 0 && (
        <div className="lp-pagination">
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0}>← Previous</button>
          <span className="lp-page-info">Page {page + 1} of {totalPages}</span>
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))} disabled={page >= totalPages - 1}>Next →</button>
          <div className="lp-page-size">
            <label htmlFor="cl-pageSize">Rows:</label>
            <select id="cl-pageSize" value={rowsPerPage} onChange={(e) => { setPage(0); setRowsPerPage(Number(e.target.value)); }}>
              {[10, 20, 30, 50].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseList;
